/**
 * Level 13: Clockwork Tower
 * 
 * Steampunk mechanical tower with precision platforming:
 * - Lots of moving platforms (gears)
 * - Precise timing required
 * - Bronze/copper aesthetic
 * - Vertical progression
 * 
 * @module configs/levels/Level13_ClockworkTower
 */

/** @type {import('../LevelTypes').LevelConfig} */
const Level13_ClockworkTower = {
    id: 13,
    name: 'Clockwork Tower',
    subtitle: 'Precision Timing',
    description: 'Navigate the gears of this ancient mechanical tower.',
    
    worldWidth: 3800,
    worldHeight: 2200,
    playerStart: { x: 150, y: 2000 },
    gravity: 1.0,
    
    theme: {
        bgGradient: ['#2c1810', '#4a3020'],  // Dark bronze
        platformColor: 0xcd7f32,  // Bronze
        accentColor: 0xffd700,
        particleColor: 0xdaa520
    },
    
    platforms: [
        // Ground floor - entry
        { x: 0, y: 2100, w: 400, h: 60 },
        { x: 500, y: 1950, w: 180, h: 25 },
        
        // First gear room
        { x: 800, y: 1800, w: 250, h: 35 },
        { x: 1100, y: 1650, w: 180, h: 25 },
        
        // Second floor
        { x: 1400, y: 1500, w: 300, h: 40 },
        { x: 1200, y: 1350, w: 160, h: 25 },
        { x: 900, y: 1200, w: 200, h: 30 },
        
        // Third floor - pendulum section
        { x: 600, y: 1050, w: 250, h: 35 },
        { x: 900, y: 900, w: 180, h: 25 },
        { x: 1200, y: 750, w: 200, h: 30 },
        
        // Fourth floor
        { x: 1500, y: 600, w: 300, h: 40 },
        { x: 1800, y: 450, w: 180, h: 25 },
        { x: 2100, y: 600, w: 200, h: 30 },
        
        // Clock face area
        { x: 2400, y: 750, w: 280, h: 35 },
        { x: 2700, y: 600, w: 200, h: 25 },
        { x: 3000, y: 450, w: 250, h: 35 },
        
        // Tower top
        { x: 3300, y: 300, w: 200, h: 30 },
        { x: 3500, y: 450, w: 300, h: 50 }
    ],
    
    // Many moving platforms representing gears
    movingPlatforms: [
        // First gear room
        { x: 650, y: 1870, w: 120, h: 20, moveX: 140, speed: 0.003 },
        { x: 1000, y: 1720, w: 100, h: 20, moveY: 120, speed: 0.0035 },
        
        // Second floor gears
        { x: 1050, y: 1420, w: 110, h: 20, moveX: 130, speed: 0.0025 },
        
        // Third floor pendulums
        { x: 750, y: 1130, w: 120, h: 20, moveX: 160, speed: 0.004 },
        { x: 1050, y: 820, w: 110, h: 20, moveY: 100, speed: 0.003 },
        
        // Clock hands
        { x: 1650, y: 530, w: 130, h: 20, moveX: 180, speed: 0.0035 },
        { x: 1950, y: 520, w: 120, h: 20, moveY: 140, speed: 0.003 },
        
        // Final ascent
        { x: 2550, y: 680, w: 110, h: 20, moveX: 150, speed: 0.003 },
        { x: 2850, y: 520, w: 120, h: 20, moveY: 120, speed: 0.0025 },
        { x: 3150, y: 370, w: 130, h: 20, moveX: 140, speed: 0.0035 }
    ],
    
    spikes: [
        { x: 850, y: 1800 },
        { x: 900, y: 1800 },
        { x: 1450, y: 1500 },
        { x: 1550, y: 600 },
        { x: 3050, y: 450 }
    ],
    
    stars: [
        { x: 1100, y: 1550 },
        { x: 650, y: 950 },
        { x: 1200, y: 650 },
        { x: 2700, y: 500 },
        { x: 3400, y: 200 }
    ],
    
    enemies: [
        { x: 900, y: 1750 },
        { x: 1500, y: 1450 },
        { x: 2500, y: 700 }
    ],
    
    door: { x: 3700, y: 300, w: 50, h: 300 },
    button: { x: 3600, y: 445 },
    box: { x: 3350, y: 200, floating: true },  // Magnetic gear piece
    goal: { x: 3750, y: 350 },

    decorations: [
        { key: 'decor-gear', centerInLevel: true, scale: 9, alpha: 0.2, scrollFactor: 0.18, depth: -50 }
    ],
    
    trampolines: [
        { x: 350, y: 2100, w: 80 },
        { x: 550, y: 1050, w: 80 }
    ],
    
    portals: [
        { x1: 250, y1: 2050, x2: 2350, y2: 700, color: 0xffd700 }  // Skip to upper floors
    ],
    
    speedrunTargets: { gold: 60, silver: 85, bronze: 110 }
};

export default Level13_ClockworkTower;
